import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../firebase';
import { onIdTokenChanged, signOut } from 'firebase/auth';

// Create the context
const AuthContext = createContext(null);

// Custom hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 🔑 IMPORTANT: use onIdTokenChanged (not onAuthStateChanged)
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      console.log(
        '🔄 Token/Auth change:',
        user ? `User: ${user.uid}` : 'No user'
      );

      if (user) {
        try {
          // 🔄 Always get a fresh token (Firebase auto-refreshes)
          const token = await user.getIdToken();
          sessionStorage.setItem('token', token);

          // Load or create user object
          const storedUser = sessionStorage.getItem('user');
          if (storedUser) {
            setCurrentUser(JSON.parse(storedUser));
          } else {
            const basicUser = {
              uid: user.uid,
              email: user.email,
              name: user.displayName || user.email?.split('@')[0],
              profession: 'General User'
            };

            setCurrentUser(basicUser);
            sessionStorage.setItem('user', JSON.stringify(basicUser));
          }
        } catch (error) {
          console.error('❌ Token refresh failed:', error);
          await signOut(auth);
          sessionStorage.clear();
          setCurrentUser(null);
        }
      } else {
        // User logged out
        setCurrentUser(null);
        sessionStorage.clear();
      }

      setLoading(false);
    });

    // ⏰ AUTO-REFRESH LOGIC (Persistence Fix)
    // 1. Refresh every 55 minutes
    const REFRESH_INTERVAL = 55 * 60 * 1000;
    const intervalId = setInterval(async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          console.log('⏰ [AuthContext] 55m interval: Forcing token refresh...');
          await user.getIdToken(true); // Force refresh
        } catch (e) {
          console.error('❌ [AuthContext] Interval refresh failed:', e);
        }
      }
    }, REFRESH_INTERVAL);

    // 2. Refresh on Tab Focus (Visibility Change)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const user = auth.currentUser;
        if (user) {
          try {
            console.log('👀 [AuthContext] Tab active: Checking/Refreshing token...');
            // existing token check is implicit, strictly force refresh to be safe
            await user.getIdToken(true);
          } catch (e) {
            console.error('❌ [AuthContext] Visibility refresh failed:', e);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      sessionStorage.clear();
    } catch (error) {
      console.error('❌ Logout failed:', error);
    }
  };

  const value = {
    currentUser,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
